import axios from "axios";
import throttle from "lodash-es/throttle";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Asset } from "app/types";
import { fetchAuthToken } from "app/Auth";
import { client } from "common/apiClient";
import { UserType } from "features/user/types";
import {
  ParentPiece,
  ProjectEntity,
  ParentPieceReport,
  ParentPieceComment,
  FeedbackHistoryItem,
  ParentPieceCommentItem,
  GetActivitiesResponse,
  GetAIChatSessionsResponse,
  GetAIChatMessagesResponse,
} from "./types";

// ============================================================
// Icon Metadata
// ============================================================

export interface IconMetadata {
  source: string;
  character?: string;
  style?: string;
  expression?: string;
  accessory?: string;
  background?: string;
  file_size?: number;
  // アップロード用フィールド
  filename?: string;
  content_type?: string;
  width?: string;
  height?: string;
}

// ============================================================
// Child Account APIs (from user/api.ts)
// ============================================================

export const createChildAccount = createAsyncThunk(
  "player/createChildAccount",
  async (
    {
      user,
    }: {
      user: {
        parent_uid: string;
        display_name: string;
        locale: string;
        env: string;
        // プロフィール情報
        icon?: string;
        birthday?: string;
        gender?: string;
        favorite_color?: string[];
        best_subject?: string[];
        glasses?: string;
        accessory?: string[];
        hairstyle?: string;
        body_type?: string;
        face_shape?: string;
        self_introduction?: string;
        // AI生成アイコンのメタ情報
        icon_metadata?: IconMetadata;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const token = await fetchAuthToken();
      const response = await client({ token: token }).post(
        "/v1/user/account/child",
        user,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return rejectWithValue({
          code: error.response.data?.code || "QUOTA_EXCEEDED",
          message:
            error.response.data?.message || "プレイヤー数の上限に達しました",
        });
      }
      throw error;
    }
  },
);

export interface ProfileData {
  birthday?: string;
  gender?: string;
  locale?: string;
  // 容姿
  glasses?: string;
  hairstyle?: string;
  body_type?: string;
  face_shape?: string;
  // お勤め先
  company_size?: string;
  industry?: string;
  occupation?: string;
  job_title?: string;
  position?: string;
  organization?: string;
  // 住所
  is_overseas?: boolean;
  region?: string;
  city?: string;
  // 家族
  family_structure?: string;
  family_roles?: string[];
  children_count?: number;
  boys_count?: number;
  girls_count?: number;
  spouse?: boolean;
  // その他
  annual_income?: string;
  self_introduction?: string;
}

export const updateUser = createAsyncThunk(
  "player/updateUser",
  throttle(
    async ({
      uid,
      userInfo,
    }: {
      uid: string;
      userInfo: {
        icon?: string;
        name?: string;
        birthday?: string;
        gender?: string;
        favorite_color?: string[];
        best_subject?: string[];
        glasses?: string;
        accessory?: string[];
        hairstyle?: string;
        body_type?: string;
        face_shape?: string;
        self_introduction?: string;
      };
    }) => {
      const token = await fetchAuthToken();
      const response = await client({ token: token }).put(
        `/v1/user/account/child/${uid}`,
        userInfo,
      );
      return response.data;
    },
    1000,
  ),
);

export const deleteChild = createAsyncThunk(
  "player/deleteChild",
  async ({ uid, parent_uid }: { uid: string; parent_uid: string }) => {
    const token = await fetchAuthToken();
    await client({ token: token }).delete(
      `/v1/user/account/child/${uid}?parent_uid=${parent_uid}`,
    );
    return uid;
  },
);

export const updateChildStatus = createAsyncThunk(
  "player/updateChildStatus",
  async ({ uid, type }: { uid: string; type: UserType }) => {
    const token = await fetchAuthToken();
    const response = await client({ token }).put(
      `/v1/user/account/child/${uid}/status`,
      { type },
    );
    return response.data;
  },
);

// ============================================================
// User Assets API (from user/api.ts)
// ============================================================

export type AssetsResponse = {
  assets: Asset[];
  totalSize: number;
};

export const getUserAssets = async ({
  uid,
}: {
  uid: string;
}): Promise<AssetsResponse> => {
  const token = await fetchAuthToken();
  const response = await client({ token: token }).get(
    `/v1/asset/user/${uid}/files`,
  );
  return response.data;
};

// ============================================================
// Activity & AI Chat APIs (from shared/api.ts)
// ============================================================

/**
 * フィードバック履歴取得
 */
export const getFeedbackHistory = async ({
  uid,
  limit = 20,
  offset = 0,
}: {
  uid: string;
  limit?: number;
  offset?: number;
}): Promise<FeedbackHistoryItem[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get<FeedbackHistoryItem[]>(
    `/v1/review/history/${uid}?limit=${limit}&offset=${offset}`,
  );
  return response.data;
};

/**
 * ユーザー活動履歴取得
 */
export const getUserActivities = async ({
  parentUid,
  childUid,
  limit = 5,
}: {
  parentUid: string;
  childUid: string;
  limit?: number;
}): Promise<GetActivitiesResponse> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get<GetActivitiesResponse>(
    `/v1/user/activity/${parentUid}/child/${childUid}?limit=${limit}`,
  );
  return response.data;
};

/**
 * 子どものAIチャットセッション一覧取得
 */
export const getChildAIChatSessions = async ({
  parentUid,
  childUid,
  limit = 20,
  offset = 0,
}: {
  parentUid: string;
  childUid: string;
  limit?: number;
  offset?: number;
}): Promise<GetAIChatSessionsResponse> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get<GetAIChatSessionsResponse>(
    `/v1/parents/${parentUid}/children/${childUid}/ai-chat/sessions?limit=${limit}&offset=${offset}`,
  );
  return response.data;
};

/**
 * AIチャットセッションのメッセージ取得
 */
export const getAIChatMessages = async ({
  parentUid,
  sessionId,
  limit = 50,
  beforeId,
}: {
  parentUid: string;
  sessionId: string;
  limit?: number;
  beforeId?: string;
}): Promise<GetAIChatMessagesResponse> => {
  const token = await fetchAuthToken();
  let url = `/v1/parents/${parentUid}/ai-chat/sessions/${sessionId}/messages?limit=${limit}`;
  if (beforeId) {
    url += `&before_id=${beforeId}`;
  }
  const response = await client({ token }).get<GetAIChatMessagesResponse>(url);
  return response.data;
};

/**
 * ユーザーのプロジェクト一覧取得（その子のものだけ。みんなのアプリは含めない）
 */
export const getUserProjects = async (uid: string): Promise<ProjectEntity[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(
    `/v1/projects/user/${uid}?include_shared=false`,
  );
  return response.data;
};

/**
 * 子どものピース一覧
 */
export const getUserPieces = async (uid: string): Promise<ParentPiece[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/user/${uid}`);
  return response.data;
};

/**
 * ピース1件（子どものピースに無いもの＝他人のピースも見られるように）
 */
export const getPiece = async (pieceId: string): Promise<ParentPiece> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/${pieceId}`);
  return response.data;
};

/**
 * アプリ1件（通報したあとにブロックされ、一覧から消えたアプリも見られるように）
 */
export const getProject = async (projectId: string): Promise<ProjectEntity> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/projects/${projectId}`);
  return response.data;
};

/**
 * ピースに付いたコメント一覧
 */
export const getPieceComments = async (
  pieceId: string,
): Promise<ParentPieceCommentItem[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/${pieceId}/comments?limit=50`);
  return response.data;
};

/**
 * 子どもが書いたピースのコメント一覧
 */
export const getUserPieceComments = async (uid: string): Promise<ParentPieceComment[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/user/${uid}/comments`);
  return response.data;
};

/**
 * 子どもが出した通報の履歴
 */
export const getUserPieceReports = async (uid: string): Promise<ParentPieceReport[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/pieces/user/${uid}/reports`);
  return response.data;
};

/**
 * ユーザーステータス取得
 */
export const getUserStatus = async (uid: string) => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/user/status/${uid}`);
  return response.data.data;
};
